import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import { PromoRequestCreated, PromoRequestRow } from "../../../../services/v2/promoApplications.service";
import useEnlaceDirecto from "../../../../utils/enlaceDirecto";
import useEsTelefono from "../../../../utils/esTelefono";
import { buildPages, errorText } from "../../managment/enrollmentRequests/helpers";
import { EnCamino } from "../../../../components/promos/tipos";
import { EXTRA_TABS, ExtraTabKey, PendingRow, PromoApplicationFiltersData, PromoApplicationRow, TabCounts, TabKey } from "./interfaces";
import DetalleAcuerdo, { AcuerdoRow } from "./detalleAcuerdo";
import Grupos from "../promotions/grupos";
import Limpieza from "../promotions/limpieza";
import ListaPorAprobar, { contarPorAprobar } from "../../../../components/aprobaciones/listaPorAprobar";
import PromoApplicationsView, { FilaPorAplicar } from "./promoApplicationsView";
import { Filtros, FILTROS_VACIOS, fechasDe, hayFiltros } from "./filtrosPromos";
import { DetalleAplicada, DetalleDevuelta, DetalleEnCamino, DetallePendiente } from "./detallePromo";
import S from "./strings";
import SolicitarPromocion, { SolicitarPromoInicial } from "./solicitarPromocion";
import ConfirmarPlantel, { PlantelElegido } from "../../managment/enrollmentRequests/confirmarPlantel";
import "../promotions/promotions.css";
import "./promoApplications.css";

/*
	Promociones (Finanzas, ESPEC_PROMOS_ADELANTO §1/§5): el expediente de cada promo de adelanto aplicada —quién la gestionó
	(asesoras con %), quién cobró, el pago, qué regaló (meses, kit o curso) y su estado. Solo cuenta lo Aplicado.
	El servidor decide qué ve cada quien (sección promoApplications); aquí se lista y se abre el detalle en ventana.
	La Solicitud de Promoción de la asesora («Solicitar Promoción», docs/ESPEC_SOLICITUD_PROMO.md) llega a Por Aplicar como ticket;
	las que Caja devolvió salen arriba de Por Aplicar, y en su ventana va «Enviar Comprobante Nuevo».
	Módulos Claros (2026-11-01): lo de cada alumna que vivía en Descuentos llega aquí: Acuerdos por Matrícula (la asesora ve los suyos;
	supervisión, Caja y dirección todo el plantel; lo decide el servidor), Por Resolver y Por Aprobar (solo con algo pendiente) y la
	Limpieza para Caja (cleanupAccess). Los enlaces llegan con ?vista=acuerdos|limpieza|porResolver|porAprobar.
*/
const VISTAS: Record<string, ExtraTabKey> = { acuerdos: "acuerdos", limpieza: "limpieza", porResolver: "porResolver", grupos: "porResolver", porAprobar: "porAprobar" };
const vistaDelEnlace = (): ExtraTabKey | null => VISTAS[new URLSearchParams(window.location.search).get("vista") || ""] || null;
const SIN_LISTA: TabKey[] = ["porResolver", "porAprobar", "limpieza"];

const http = HttpManager.getInstance();
//100 por pantalla como Matrículas; En Camino da hasta 100.
const PAGE_SIZES = [100, 200];
const TABS_EN_CAMINO: TabKey[] = ["enCamino", "fallidas"];

type Abierto = { tipo: "aplicada"; id: number } | { tipo: "enCamino"; id: number; vivas: boolean } | { tipo: "acuerdo"; row: AcuerdoRow } | FilaPorAplicar;
type Datos = { tab: TabKey; rows: any[]; total: number; perPage: number; totals?: any };

/**
	Carga al instante (2026-11-02, «Por Aplicar se tarda en cargar»): el servidor contesta en ~0.1 s, la espera era la pantalla vacía en cada
	visita y el brinco a Aplicadas que pedía otra lista DESPUÉS de la primera. Lo último que llegó de cada lista (por plantel, pestaña y
	filtros) y los números de las pestañas se guardan en memoria: al volver se pintan ya, y lo nuevo los reemplaza al llegar. Solo en
	memoria (no en el navegador): al recargar la página se pide todo de nuevo.
*/
type Lista = { datos: Datos; tickets?: number[]; counts?: TabCounts | null; options?: PromoApplicationFiltersData | null; sym?: string; canCreate?: boolean;
	/** Cuándo llegó, y si la pidió la pantalla por adelantado (Aplicadas mientras carga Por Aplicar) sin que nadie la haya pintado aún. */
	en?: number; adelantada?: boolean };
/** Lo adelantado se usa tal cual (sin volver a pedirlo) solo si llegó hace menos de esto. */
const ADELANTADA_VALE_MS = 10000;
const listas = new Map<string, Lista>();
const enCurso = new Map<string, Promise<Lista>>();
const MAX_LISTAS = 40;
type Contadores = { porAplicarVacia?: boolean; devueltas?: PromoRequestRow[]; enCaminoTotal?: number | null; nResolver?: number; nAprobar?: number; limpieza?: { canSee: boolean; pending: number } | null };
const contadores = new Map<number, Contadores>();
const contadoresDe = (campusId: number | null): Contadores => (campusId ? contadores.get(campusId) : undefined) || {};
const guardarContador = (campusId: number, c: Contadores) => contadores.set(campusId, { ...contadoresDe(campusId), ...c });

type Pedido = { campusId: number; tab: TabKey; acuerdosEstado: string; filtros: Filtros; q?: string; page: number; perPage: number };
const llaveDe = (p: Pedido): string => JSON.stringify([p.campusId, p.tab, p.tab === "acuerdos" ? p.acuerdosEstado : "", fechasDe(p.filtros),
	p.filtros.promotionId, p.filtros.advisorId, p.q || "", p.page, p.perPage]);

/** Pide la lista de una pestaña y la guarda; dos pedidos iguales al mismo tiempo comparten la misma petición. */
const pedirLista = (p: Pedido, adelantada = false): Promise<Lista> => {
	const llave = llaveDe(p);
	const ya = enCurso.get(llave);
	if (ya) return ya;
	const { campusId, tab, q, page, perPage } = p;
	const pedido: Promise<Lista> = (tab === "acuerdos"
		? http.paymentService.agreements(campusId, { status: p.acuerdosEstado, q, page, perPage })
			.then((r: any): Lista => ({ datos: { tab, rows: r.data?.rows || [], total: Number(r.data?.total) || 0, perPage: Number(r.data?.perPage) || perPage } }))
		: TABS_EN_CAMINO.includes(tab)
		? http.promoApplicationsService.enCaminoList(campusId, { status: tab === "fallidas" ? "fallidas" : "vivas", q, page, perPage: Math.min(perPage, 100) })
			.then((r): Lista => ({ datos: { tab, rows: r.data.rows || [], total: Number(r.data.total) || 0, perPage: Number(r.data.perPage) || 25 } }))
		: http.promoApplicationsService.list(campusId, { ...fechasDe(p.filtros), promotionId: p.filtros.promotionId, advisorId: p.filtros.advisorId, q, page, perPage, tab })
			.then((r: any): Lista => ({
				datos: { tab, rows: r.data?.data || [], total: Number(r.data?.total) || 0, perPage: Number(r.data?.perPage) || perPage, totals: r.data?.totals || null },
				tickets: tab === "porAplicar" ? (r.data?.data || []).map((x: PendingRow) => Number(x.ticketId)).filter(Boolean) : undefined,
				counts: r.data?.counts || null, options: r.data?.filters || null, sym: r.data?.currencySymbol || undefined,
				canCreate: r.data?.canCreate !== undefined ? !!r.data.canCreate : undefined,
			})))
		.then((l) => {
			l.en = Date.now();
			l.adelantada = adelantada;
			listas.delete(llave);
			listas.set(llave, l);
			if (listas.size > MAX_LISTAS) listas.delete(listas.keys().next().value as string);
			return l;
		})
		.finally(() => { enCurso.delete(llave); });
	enCurso.set(llave, pedido);
	return pedido;
};

const PromoApplicationsController: React.FC = () => {
	const movil = useEsTelefono();
	const navigate = useNavigate();
	const location = useLocation();
	const perfil: any = useSelector((state: RootState) => (state as any).userProfile?.value);
	const yoId: number | null = useSelector((state: RootState) => (state.rootReducer.sessionState as any).user?.id ?? null);
	const esSuper: boolean = perfil?.currentRole === "superAdmin";
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;
	const campusSym: string = sessionCampus?.currency || legacyCampus?.currency || "";

	const vistaEnlace = useRef<ExtraTabKey | null>(vistaDelEnlace());
	// Si la última vez Por Aplicar estaba vacía (y brincó a Aplicadas), se abre ya en Aplicadas; con números nuevos se regresa si hace falta.
	const [tab, setTab] = useState<TabKey>(vistaEnlace.current || (contadoresDe(campusId).porAplicarVacia ? "aplicadas" : "porAplicar"));
	const tocoPestana = useRef<boolean>(!!vistaEnlace.current);
	const abrioEnAplicadas = useRef<boolean>(!vistaEnlace.current && !!contadoresDe(campusId).porAplicarVacia);
	const [acuerdosEstado, setAcuerdosEstado] = useState<string>("vivos");
	const [nResolver, setNResolver] = useState<number>(contadoresDe(campusId).nResolver || 0);
	const [nAprobar, setNAprobar] = useState<number>(contadoresDe(campusId).nAprobar || 0);
	const [limpiezaAcc, setLimpiezaAcc] = useState<{ canSee: boolean; pending: number } | null>(contadoresDe(campusId).limpieza ?? null);
	const [filtros, setFiltros] = useState<Filtros>(FILTROS_VACIOS);
	const [search, setSearch] = useState<string>("");
	const [appliedSearch, setAppliedSearch] = useState<string>("");
	const [page, setPage] = useState<number>(1);
	const [perPage, setPerPage] = useState<number>(PAGE_SIZES[0]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [counts, setCounts] = useState<TabCounts | null>(null);
	const [options, setOptions] = useState<PromoApplicationFiltersData | null>(null);
	const [sym, setSym] = useState<string>(campusSym);
	const [canCreateSrv, setCanCreateSrv] = useState<boolean | undefined>(undefined);
	/** Lo que llegó del servidor y de qué pestaña es: nunca se pinta la lista de otra pestaña mientras llega la nueva. */
	const [datos, setDatos] = useState<Datos | null>(null);
	const [devueltas, setDevueltas] = useState<PromoRequestRow[] | null>(contadoresDe(campusId).devueltas ?? null);
	const [enCaminoTotal, setEnCaminoTotal] = useState<number | null>(contadoresDe(campusId).enCaminoTotal ?? null);
	const [refresco, setRefresco] = useState<number>(0);
	const [abierto, setAbierto] = useState<Abierto | null>(null);
	/** Aviso verde de arriba (reenvío a Caja), como el de Matrículas. */
	const [aviso, setAviso] = useState<string | null>(null);
	/** Tickets pendientes de la última lista de Por Aplicar: una devuelta que sigue pendiente no se pinta ni se cuenta dos veces. */
	const [ticketsPendientes, setTicketsPendientes] = useState<number[]>([]);
	/** Nueva Promoción: primero se confirma el plantel (como Matrículas), luego el formulario. */
	const [pedirPlantel, setPedirPlantel] = useState<{ inicial: SolicitarPromoInicial | null } | null>(null);
	const [solicitar, setSolicitar] = useState<{ inicial: SolicitarPromoInicial | null; plantel: PlantelElegido } | null>(null);
	const plantelPrevio = useRef<number | null | undefined>(campusId);
	/** El brinco a Aplicadas solo se decide con números recién llegados del servidor, nunca con los guardados de la visita anterior. */
	const conteosFrescos = useRef<boolean>(false);
	const devueltasFrescas = useRef<boolean>(false);
	const refrescar = () => setRefresco((x) => x + 1);

	// «Promoción» desde la ficha del CRM / WappChat: /promoApplications?plantel=X con state.solicitarPromo = { studentId, … }.
	useEffect(() => {
		const n = (location.state as any)?.solicitarPromo;
		if (!n || !Number(n.studentId)) return;
		setPedirPlantel({ inicial: { studentId: Number(n.studentId), enrollmentId: Number(n.enrollmentId) || null, name: n.name || null, identityCode: n.identityCode || null } });
		navigate(location.pathname + location.search, { replace: true, state: null });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.state]);
	// El «plantel=» del enlace cambia la barra al plantel del alumno (igual que los enlaces de tickets) y limpia la dirección.
	useEnlaceDirecto("solicitarPromo", () => { /* el alumno viene en location.state */ });

	useEffect(() => {
		// Solo al CAMBIAR de plantel (no al montar): si no, reinicia la pestaña mientras corre la primera carga.
		if (plantelPrevio.current === campusId) return;
		plantelPrevio.current = campusId;
		// El enlace de un aviso (?vista=…&plantel=X) cambia el plantel después de montar: su pestaña se respeta una vez.
		const v = vistaEnlace.current;
		vistaEnlace.current = null;
		tocoPestana.current = !!v;
		conteosFrescos.current = false; devueltasFrescas.current = false;
		const c = contadoresDe(campusId);
		abrioEnAplicadas.current = !v && !!c.porAplicarVacia;
		setDatos(null); setOptions(null); setCounts(null); setAbierto(null); setPage(1); setFiltros(FILTROS_VACIOS); setTab(v || (c.porAplicarVacia ? "aplicadas" : "porAplicar"));
		setDevueltas(c.devueltas ?? null); setCanCreateSrv(undefined); setSym(campusSym); setTicketsPendientes([]); setAviso(null);
		setNResolver(c.nResolver || 0); setNAprobar(c.nAprobar || 0); setLimpiezaAcc(c.limpieza ?? null); setEnCaminoTotal(c.enCaminoTotal ?? null);
	}, [campusId]);

	// La lista de la pestaña. Solo vale la respuesta de la última petición; lo guardado de la visita anterior se pinta mientras llega.
	useEffect(() => {
		if (!campusId) return;
		if (SIN_LISTA.includes(tab)) { setLoading(false); setError(null); return; }
		let vigente = true;
		setLoading(true);
		const pedido: Pedido = { campusId, tab, acuerdosEstado, filtros, q: appliedSearch || undefined, page, perPage };
		const pintar = (l: Lista, fresca: boolean) => {
			setDatos(l.datos);
			if (l.tickets) setTicketsPendientes(l.tickets);
			// Los números guardados solo rellenan mientras no haya llegado nada del servidor: nunca pisan unos recién llegados.
			if (l.counts !== undefined && (fresca || !conteosFrescos.current)) { setOptions(l.options || null); setCounts(l.counts); if (fresca) conteosFrescos.current = true; }
			if (l.sym) setSym(l.sym);
			if (l.canCreate !== undefined) setCanCreateSrv(l.canCreate);
		};
		const guardada = listas.get(llaveDe(pedido));
		if (guardada && guardada.adelantada && Date.now() - (guardada.en || 0) < ADELANTADA_VALE_MS) {
			// Recién llegada por adelantado (el brinco a Aplicadas): ya es lo nuevo, no se vuelve a pedir.
			guardada.adelantada = false;
			pintar(guardada, true); setError(null); setLoading(false);
			return;
		}
		if (guardada) pintar(guardada, false);
		// Al abrir en Por Aplicar sin haber elegido pestaña, Aplicadas se pide a la vez: si Por Aplicar viene vacía, el brinco ya la tiene lista.
		if (tab === "porAplicar" && !tocoPestana.current && page === 1 && !appliedSearch && !hayFiltros(filtros)) {
			pedirLista({ ...pedido, tab: "aplicadas" }, true).catch(() => undefined);
		}
		const pedir = pedirLista(pedido).then((l) => { l.adelantada = false; if (vigente) pintar(l, true); });
		pedir.then(() => { if (vigente) setError(null); })
			// Si falla, la pestaña queda vacía con el aviso de error arriba (no una rueda que nunca termina).
			.catch((e: any) => { if (vigente) { setError(errorText(e)); setDatos((d) => d && d.tab === tab ? d : { tab, rows: [], total: 0, perPage }); } })
			.finally(() => { if (vigente) setLoading(false); });
		return () => { vigente = false; };
	}, [campusId, tab, filtros, appliedSearch, page, perPage, refresco, acuerdosEstado]);

	// Por Resolver (grupos), Por Aprobar y Limpieza: sus pestañas salen solo si hay algo (Limpieza: a quien la hace).
	useEffect(() => {
		if (!campusId) return;
		let vigente = true;
		http.paymentService.discountGroupsCount(campusId).then((r: any) => Number(r.data?.open) || 0).catch(() => 0)
			.then((n) => { guardarContador(campusId, { nResolver: n }); if (vigente) setNResolver(n); });
		contarPorAprobar(campusId).then((n) => { guardarContador(campusId, { nAprobar: n }); if (vigente) setNAprobar(n); });
		http.paymentService.cleanupAccess(campusId).then((r: any) => ({ canSee: !!r.data?.canSee, pending: Number(r.data?.pending) || 0 })).catch(() => null)
			.then((acc) => { guardarContador(campusId, { limpieza: acc }); if (vigente) setLimpiezaAcc(acc); });
		return () => { vigente = false; };
	}, [campusId, refresco]);

	// Número de En Camino (globito de la pestaña). Un servidor sin este endpoint no estorba.
	useEffect(() => {
		if (!campusId) { setEnCaminoTotal(null); return; }
		let vigente = true;
		http.promoApplicationsService.enCaminoList(campusId, { status: "vivas", page: 1, perPage: 1 })
			.then((r) => Number(r.data?.total) || 0).catch(() => null)
			.then((n) => { guardarContador(campusId, { enCaminoTotal: n }); if (vigente) setEnCaminoTotal(n); });
		return () => { vigente = false; };
	}, [campusId, refresco]);

	// Devueltas por Caja: arriba de Por Aplicar. Se piden siempre para que el número de la pestaña las cuente.
	useEffect(() => {
		if (!campusId) { setDevueltas(null); return; }
		let vigente = true;
		http.promoApplicationsService.requests(campusId, { status: "returned", perPage: 200 })
			.then((r) => r.data?.data || []).catch((): PromoRequestRow[] => [])
			.then((rows) => { guardarContador(campusId, { devueltas: rows }); if (vigente) { devueltasFrescas.current = true; setDevueltas(rows); } });
		return () => { vigente = false; };
	}, [campusId, refresco]);

	// Sin nada por aplicar NI devuelto, y sin haber elegido pestaña, abre en Aplicadas (como Matrículas).
	useEffect(() => {
		if (tocoPestana.current || !counts || devueltas === null || !conteosFrescos.current || !devueltasFrescas.current) return;
		const vacia = Number(counts.porAplicar) === 0 && devueltas.length === 0;
		if (campusId) guardarContador(campusId, { porAplicarVacia: vacia });
		if (tab === "porAplicar" && vacia) setTab("aplicadas");
		// Abrió en Aplicadas por la visita anterior y ahora sí hay algo por aplicar: regresa a Por Aplicar (una vez por visita).
		if (tab === "aplicadas" && abrioEnAplicadas.current) {
			abrioEnAplicadas.current = false;
			if (!vacia) setTab("porAplicar");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [counts, devueltas]);

	const cambiarPestana = (t: TabKey) => {
		tocoPestana.current = true;
		vistaEnlace.current = null;
		if (t === tab) return;
		setTab(t); setPage(1); setDatos(null);
		// Por Aplicar no tiene fechas: al llegar ahí se quitan; los demás filtros se quedan.
		if (t === "porAplicar") setFiltros((f) => ({ ...f, preset: "", from: "", to: "" }));
	};

	const devueltasIds = useMemo(() => new Set((devueltas || []).map((r) => r.intentId)), [devueltas]);
	const repetidas = ticketsPendientes.filter((id) => devueltasIds.has(id)).length;
	const cuenta = useMemo(() => counts ? { ...counts, porAplicar: Math.max(0, (Number(counts.porAplicar) || 0) + (devueltas?.length || 0) - repetidas) } as TabCounts : null, [counts, devueltas, repetidas]);
	const filas = datos && datos.tab === tab ? datos : null;
	// Las devueltas se filtran aquí con lo mismo que la lista (buscador, promoción, asesora); el servidor no las filtra.
	const devueltaPasa = (r: PromoRequestRow) => {
		const t = appliedSearch.toLowerCase();
		if (t && ![r.student.name, r.student.identityCode || "", r.promoName, String(r.intentId)].some((x) => (x || "").toLowerCase().includes(t))) return false;
		if (filtros.promotionId && Number(r.promotionId) !== Number(filtros.promotionId)) return false;
		if (filtros.advisorId && !(r.advisors || []).some((a) => Number(a.userId) === Number(filtros.advisorId))) return false;
		return true;
	};
	const nDevueltas = tab === "porAplicar" ? (devueltas || []).filter(devueltaPasa) : [];
	// Van arriba de la primera página; en las demás no se repiten, pero sí se cuentan en el total y en los números de renglón.
	const devueltasVisibles = page === 1 ? nDevueltas : [];
	const porAplicar: FilaPorAplicar[] | null = tab === "porAplicar" && filas
		? [...devueltasVisibles.map((r) => ({ tipo: "devuelta" as const, r })),
			...(filas.rows as PendingRow[]).filter((r) => !(r.ticketId && devueltasIds.has(r.ticketId))).map((r) => ({ tipo: "pendiente" as const, r }))] : null;

	const per = filas?.perPage || perPage;
	const lastPage = Math.max(1, Math.ceil((filas?.total || 0) / Math.max(1, per)));
	const pages = useMemo(() => buildPages(page, lastPage, (n) => { if (n >= 1 && n <= lastPage) setPage(n); }, movil), [page, lastPage, movil]);
	const enPagina = tab === "porAplicar" ? (porAplicar?.length || 0) : (filas?.rows.length || 0);
	const desde = page === 1 ? 1 : nDevueltas.length + (page - 1) * per + 1;
	const itemCount = { start: enPagina === 0 ? 0 : desde, end: enPagina === 0 ? 0 : desde + enPagina - 1, total: filas ? Math.max(0, filas.total + nDevueltas.length - repetidas) : 0 };

	const reenviar = async (intentId: number, files: File[], notes: string): Promise<PromoRequestCreated> => {
		const form = new FormData();
		files.forEach((f) => form.append("files[]", f, f.name));
		if (notes.trim()) form.append("notes", notes.trim());
		const r = await http.promoApplicationsService.resendRequest(campusId!, intentId, form);
		setAviso(S.resent(r.data.intentId));
		refrescar();
		return r.data;
	};
	// Sin `canCreate` del servidor (versión vieja) solo el Super Admin ve el botón.
	const canCreate: boolean = canCreateSrv !== undefined ? canCreateSrv : esSuper;

	const extraTabs: { key: ExtraTabKey; label: string; count: number }[] = [{ key: "acuerdos", label: "Acuerdos", count: 0 }];
	if (nResolver > 0 || tab === "porResolver") extraTabs.push({ key: "porResolver", label: "Por Resolver", count: nResolver });
	if (nAprobar > 0 || tab === "porAprobar") extraTabs.push({ key: "porAprobar", label: "Por Aprobar", count: nAprobar });
	if (limpiezaAcc?.canSee || tab === "limpieza") extraTabs.push({ key: "limpieza", label: "Limpieza", count: limpiezaAcc?.pending || 0 });
	const s2 = sym || campusSym;
	const cuerpo = !campusId ? null : tab === "porResolver" ? <Grupos campusId={campusId} sym={s2} onCount={setNResolver} soloCasos />
		: tab === "porAprobar" ? <ListaPorAprobar campusId={campusId} onCount={setNAprobar} />
		: tab === "limpieza" ? <Limpieza campusId={campusId} sym={s2} /> : null;

	if (!campusId) return <div className="container-fluid waModulo" style={{ padding: 24 }}>Elige un plantel.</div>;

	return (
		<>
			<PromoApplicationsView movil={movil} sym={sym || campusSym} tab={tab} counts={cuenta} enCaminoTotal={enCaminoTotal} onTab={cambiarPestana}
				search={search} onSearch={setSearch}
				onApplySearch={() => { setAppliedSearch(search.trim()); setPage(1); }}
				onClearSearch={() => { setSearch(""); setAppliedSearch(""); setPage(1); }}
				filtros={filtros} onFiltros={(f) => { setFiltros(f); setPage(1); }} options={options} loading={loading} error={error}
				aviso={aviso} onCerrarAviso={() => setAviso(null)}
				aplicadas={filas && !TABS_EN_CAMINO.includes(tab) && tab !== "porAplicar" && !EXTRA_TABS.includes(tab as ExtraTabKey) ? filas.rows as PromoApplicationRow[] : null}
				totals={filas?.totals || null}
				porAplicar={porAplicar}
				enCamino={filas && TABS_EN_CAMINO.includes(tab) ? filas.rows as EnCamino[] : null}
				pages={pages} pageSizes={PAGE_SIZES} itemCount={itemCount} onPageSize={(n) => { setPerPage(n); setPage(1); }}
				canCreate={canCreate} onNueva={() => setPedirPlantel({ inicial: null })}
				onAbrirAplicada={(r) => setAbierto({ tipo: "aplicada", id: r.id })}
				onAbrirEnCamino={(e) => setAbierto({ tipo: "enCamino", id: e.id, vivas: tab === "enCamino" })}
				onAbrirPorAplicar={(f) => setAbierto(f)}
				extraTabs={extraTabs} cuerpo={cuerpo}
				acuerdos={filas && tab === "acuerdos" ? filas.rows as AcuerdoRow[] : null} acuerdosEstado={acuerdosEstado}
				onAcuerdosEstado={(v) => { setAcuerdosEstado(v); setPage(1); }} onAbrirAcuerdo={(r) => setAbierto({ tipo: "acuerdo", row: r })} />

			{abierto?.tipo === "aplicada" ? <DetalleAplicada campusId={campusId} id={abierto.id} sym={sym} onClose={() => setAbierto(null)} onChanged={refrescar} /> : null}
			{abierto?.tipo === "enCamino" ? <DetalleEnCamino campusId={campusId} id={abierto.id} sym={sym} vivas={abierto.vivas} onClose={() => setAbierto(null)} onChanged={refrescar} /> : null}
			{abierto?.tipo === "acuerdo" ? <DetalleAcuerdo campusId={campusId} row={abierto.row} sym={sym || campusSym} onClose={() => setAbierto(null)} onChanged={refrescar} /> : null}
			{abierto?.tipo === "pendiente" ? <DetallePendiente campusId={campusId} r={abierto.r} sym={sym} onClose={() => setAbierto(null)} /> : null}
			{abierto?.tipo === "devuelta" ? (
				<DetalleDevuelta campusId={campusId} r={abierto.r} sym={sym} onClose={() => setAbierto(null)} onReenviar={reenviar}
					puedeReenviar={esSuper || (yoId !== null && !!abierto.r.requestedBy && Number(abierto.r.requestedBy.id) === Number(yoId))} />
			) : null}

			{pedirPlantel ? (
				<ConfirmarPlantel que="promoción" sugerido={campusId} onCerrar={() => setPedirPlantel(null)}
					onElegir={(p) => { setSolicitar({ inicial: pedirPlantel.inicial, plantel: p }); setPedirPlantel(null); }} />
			) : null}
			{solicitar ? (
				<SolicitarPromocion campusId={solicitar.plantel.id} campusName={solicitar.plantel.nombre} movil={movil} inicial={solicitar.inicial}
					sym={solicitar.plantel.id === campusId ? (sym || campusSym)
						: String((Object.values(legacyCampusRef || {}) as any[]).find((c) => Number(c?.id) === solicitar.plantel.id)?.currency || campusSym)}
					onClose={() => setSolicitar(null)}
					onSent={() => { tocoPestana.current = true; setTab("porAplicar"); setPage(1); setFiltros((f) => ({ ...f, preset: "", from: "", to: "" })); refrescar(); }}
					onSentEnCaja={() => { tocoPestana.current = true; setTab("enCamino"); setPage(1); refrescar(); }} />
			) : null}
		</>
	);
};

export default PromoApplicationsController;
